import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Users } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_BADGE = {
  insider: { name: "Insider", color: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]" },
  studio: { name: "Studio", color: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20" },
  founder: { name: "Founder", color: "bg-[#111111] text-white border-[#111111]" },
};

const STATUS_BADGE = {
  active: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  cancelled: "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]/50",
  free: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
};

const StatTile = ({ label, value, sub, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-[#E7E7E2] rounded-2xl p-6 md:p-7 flex flex-col min-h-[140px]"
  >
    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/45">
      {label}
    </div>
    <div className="mt-3 font-sora text-[36px] md:text-[42px] font-extrabold leading-none tracking-tight text-[#111111]">
      {value}
    </div>
    {sub && (
      <div className="mt-auto pt-4 font-manrope text-[12.5px] text-[#111111]/55 truncate">
        {sub}
      </div>
    )}
  </div>
);

export const MembersPanel = () => {
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [list, setList] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken]
  );

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" });
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter) params.set("status", statusFilter);
      if (tierFilter) params.set("tier", tierFilter);
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/members/stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/members?${params.toString()}`, { headers: authHeaders }),
      ]);
      setStats(statsRes.data);
      setList(listRes.data);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Could not load members."
      );
    } finally {
      setLoading(false);
    }
  }, [authHeaders, page, search, statusFilter, tierFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const onDelete = async (memberId, email) => {
    if (!window.confirm(`Remove ${email}? This only removes them from your DB — not Fourthwall.`)) return;
    setDeleting(memberId);
    try {
      await axios.delete(`${API}/admin/members/${memberId}`, { headers: authHeaders });
      toast.success("Member removed");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section data-testid="members-panel" className="mt-16">
      <div className="overline">— Members</div>
      <h2 className="mt-4 font-sora text-[28px] md:text-[36px] font-bold tracking-tight text-[#111111]">
        Subscriptions & MRR.
      </h2>
      <p className="mt-3 font-manrope text-[15px] text-[#111111]/65 max-w-2xl">
        Live view of every Vaymilo membership. Numbers come from Fourthwall
        webhooks — no manual sync needed.
      </p>

      {/* Stat tiles */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
        <StatTile
          testId="member-stat-mrr"
          label="MRR"
          value={loading ? "—" : `$${stats?.mrr_usd?.toFixed?.(2) ?? "0.00"}`}
          sub={`ARR · $${(stats?.arr_usd ?? 0).toLocaleString?.() || "0"}`}
        />
        <StatTile
          testId="member-stat-active"
          label="Active members"
          value={loading ? "—" : (stats?.active ?? 0)}
          sub={stats?.latest_active ? `Latest: ${stats.latest_active.email}` : "—"}
        />
        <StatTile
          testId="member-stat-cancelled"
          label="Cancelled"
          value={loading ? "—" : (stats?.cancelled ?? 0)}
          sub={`Total ever · ${stats?.total ?? 0}`}
        />
        <StatTile
          testId="member-stat-by-tier"
          label="By tier"
          value={
            loading
              ? "—"
              : `${stats?.by_tier?.insider || 0}·${stats?.by_tier?.studio || 0}·${stats?.by_tier?.founder || 0}`
          }
          sub="Insider · Studio · Founder"
        />
      </div>

      {/* Filters */}
      <div className="mt-8 bg-white border border-[#E7E7E2] rounded-2xl p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search by email…"
            data-testid="member-search"
            className="flex-1 min-w-[200px] border border-[#E7E7E2] rounded-lg px-4 py-2.5 font-manrope text-[14px] text-[#111111] focus:border-[#2563EB] focus:bg-white outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
            data-testid="member-status-filter"
            className="border border-[#E7E7E2] rounded-lg px-3 py-2.5 font-manrope text-[14px] text-[#111111] bg-white"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
            <option value="free">Free</option>
          </select>
          <select
            value={tierFilter}
            onChange={(e) => {
              setPage(1);
              setTierFilter(e.target.value);
            }}
            data-testid="member-tier-filter"
            className="border border-[#E7E7E2] rounded-lg px-3 py-2.5 font-manrope text-[14px] text-[#111111] bg-white"
          >
            <option value="">All tiers</option>
            <option value="insider">Insider</option>
            <option value="studio">Studio</option>
            <option value="founder">Founder</option>
          </select>
        </div>

        {/* Table */}
        <div className="mt-5 -mx-5 md:-mx-6 overflow-x-auto">
          <table
            data-testid="members-table"
            className="min-w-full text-left font-manrope text-[14px]"
          >
            <thead>
              <tr className="border-y border-[#E7E7E2] bg-[#F7F7F4]">
                <th className="px-5 md:px-6 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55">
                  Email
                </th>
                <th className="px-3 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55">
                  Tier
                </th>
                <th className="px-3 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55">
                  Status
                </th>
                <th className="px-3 py-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55">
                  Since
                </th>
                <th className="px-5 md:px-6 py-3 text-right font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && list.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 md:px-6 py-10 text-center text-[#111111]/55">
                    <Users size={20} className="inline-block mr-2 align-middle" />
                    No members yet. They&rsquo;ll appear here as soon as your
                    first Fourthwall subscription comes in.
                  </td>
                </tr>
              ) : (
                list.items.map((m) => {
                  const badge = TIER_BADGE[m.tier] || null;
                  return (
                    <tr
                      key={m.id || m.email}
                      data-testid={`member-row-${m.email}`}
                      className="border-b border-[#E7E7E2] hover:bg-[#F7F7F4]"
                    >
                      <td className="px-5 md:px-6 py-4 text-[#111111]">{m.email}</td>
                      <td className="px-3 py-4">
                        {badge ? (
                          <span
                            className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 font-mono text-[10px] tracking-[0.18em] uppercase ${badge.color}`}
                          >
                            {badge.name}
                          </span>
                        ) : (
                          <span className="text-[#111111]/40">—</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 font-mono text-[10px] tracking-[0.18em] uppercase ${STATUS_BADGE[m.status] || STATUS_BADGE.free}`}
                        >
                          {m.status || "free"}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-[#111111]/65">
                        {m.subscribed_at
                          ? new Date(m.subscribed_at).toLocaleDateString()
                          : m.created_at
                          ? new Date(m.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-5 md:px-6 py-4 text-right">
                        <button
                          onClick={() => onDelete(m.id, m.email)}
                          disabled={deleting === m.id}
                          data-testid={`member-delete-${m.email}`}
                          className="inline-flex items-center gap-1.5 text-[#B91C1C] hover:text-[#7F1D1D] disabled:opacity-50 font-manrope text-[13px]"
                        >
                          <Trash2 size={13} />
                          {deleting === m.id ? "…" : "Remove"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {list.pages > 1 && (
          <div className="mt-5 flex items-center justify-between font-manrope text-[13px] text-[#111111]/65">
            <span>
              Page {list.page} of {list.pages} · {list.total} total
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={list.page <= 1}
                data-testid="member-prev-page"
                className="px-3 py-1.5 rounded border border-[#E7E7E2] disabled:opacity-40 hover:border-[#111111]"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={list.page >= list.pages}
                data-testid="member-next-page"
                className="px-3 py-1.5 rounded border border-[#E7E7E2] disabled:opacity-40 hover:border-[#111111]"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MembersPanel;
